import type { CabinetCreateAppointmentRequest, CabinetWarningCode } from "@planerius/contracts/cabinet"
import { CabinetWarningCode as WarningCodes } from "@planerius/contracts/cabinet"
import { Link, useNavigate } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"

import { useHasPermission } from "../../access/use-access"
import { fetchClients } from "../../api/clients.api"
import { createAppointment } from "../../api/journal-actions.api"
import { isApiProblem, transportProblem } from "../../api/problem"
import type { WindowSearch } from "../../journal/booking-draft"
import { bookingReducer, draftIntent, openDraft } from "../../journal/booking-draft"
import { freeWindows } from "../../journal/free-slots"
import { newIdempotencyKey } from "../../api/idempotency"
import { formatMinute } from "../../journal/journal-time"
import { formatMoney } from "../../journal/money"
import { cx } from "../../lib/cx"
import { formatDateLong } from "../../lib/format"
import { Button } from "../../ui/button"
import { Checkbox } from "../../ui/field"
import { EmptyState } from "../../ui/empty-state"
import { Skeleton } from "../../ui/skeleton"
import styles from "./master-book-screen.module.css"
import { MasterBackBar, MasterFrame, MasterNoEmployee } from "./master-frame"
import { noShowLabel, visitsLabel } from "./master-text"
import { zonedInstant } from "./master-time"
import { isCompletePhone, normalizePhone } from "./phone"
import { useMasterDay } from "./use-master-day"
import { useMasterServices } from "./use-master-services"

/**
 * 5-М8 «Записать к себе» — самостоятельный экран, а не форма администратора
 * с выключенными полями.
 *
 * ЧТО ИМЕННО ЗНАЧИТ «ОДНА КАРТОЧКА ИЛИ НИЧЕГО» (ТЗ §2.7.1.1, разведено по пунктам).
 *
 * 1. ПОИСК ИДЁТ ТОЛЬКО ПО ПОЛНОМУ НОМЕРУ. Пока номер не набран целиком, запрос
 *    НЕ УХОДИТ ВОВСЕ — не «уходит и ничего не находит», а не уходит. Форма
 *    администратора ищет от 4–5 цифр, и отдать это мастеру значит выдать ему перебор
 *    базы салона по номерам: медленная выгрузка — тоже выгрузка (GAPS E3, ADR-024).
 * 2. ОТВЕТ — ОДНА КАРТОЧКА ЛИБО НИЧЕГО. Списка не бывает. Если на полный номер
 *    сервер вернул несколько карточек (мама и ребёнок на одном телефоне), мастер
 *    не выбирает из них: он не может знать, кого записывает, а список — это ровно то,
 *    чего здесь быть не должно. Такую запись оформляет администратор.
 * 3. ВТОРОЙ ПУТЬ К КЛИЕНТУ — «МОИ КЛИЕНТЫ» (5-М4), то есть те, кого мастер обслуживал,
 *    и только они. Это отдельный экран со своим адресом, а не выпадающий список внутри
 *    формы.
 * 4. МАСТЕР — ФАКТ, А НЕ ПОЛЕ. «Мастер — вы, Анна Соколова» стоит подписью в шапке;
 *    выключенного списка мастеров здесь нет, потому что выбора нет.
 * 5. ВРЕМЯ — ТОЛЬКО ИЗ СВОЕЙ СМЕНЫ. Чужие колонки в подборе не участвуют физически:
 *    окна считаются из колонки самого мастера.
 *
 * «ВВЕДЁННОЕ НЕ ТЕРЯЕТСЯ» держит общий редьюсер журнала (journal/booking-draft.ts):
 * ни один ответ сервера не трогает черновик, кроме выбора нового окна. Здесь это правило
 * не переписано заново — оно переиспользовано, поэтому форма мастера и форма
 * администратора не могут разойтись в самом дорогом месте.
 */

/**
 * Редакция текста согласия на обработку данных.
 *
 * ЗАХАРДКОЖЕНА, И ЭТО ДЕФЕКТ КОНТРАКТА, А НЕ ЛЕНЬ. Контракт требует версию документа
 * (AdminConsentCapture.personalDataVersion) — без неё запись не создать, — но маршрута,
 * который назвал бы ДЕЙСТВУЮЩУЮ редакцию, в API нет ни одного. Дата взята из макета
 * («редакция от 1 марта 2026»). В отчёте это названо: версия согласия — юридический
 * факт, и знать его обязан сервер, иначе в день смены текста кабинет начнёт записывать
 * людям согласие на старую редакцию.
 */
const CONSENT_VERSION = "2026-03-01"
const CONSENT_LABEL = "Согласие на обработку данных получено устно · редакция от 1 марта 2026"

export interface MasterBookSearch {
  readonly date?: string | undefined
  readonly branch?: string | undefined
  /** Минута от начала дня: форма открыта из пустого промежутка ленты. */
  readonly at?: number | undefined
}

type LookupPhase = "idle" | "partial" | "searching" | "found" | "new" | "many" | "failed"

export function MasterBookScreen(props: { readonly search: MasterBookSearch }): ReactNode {
  const data = useMasterDay(props.search)
  const services = useMasterServices(data.employeeId)
  const canCreate = useHasPermission("appointments.create")
  const navigate = useNavigate()

  const [state, dispatch] = useReducer(
    bookingReducer,
    { date: data.date, startMinute: props.search.at ?? 0, employeeId: data.employeeId, durationMinutes: 0 },
    openDraft,
  )
  const [submitProblem, setSubmitProblem] = useState<string | null>(null)
  const [created, setCreated] = useState(false)
  const keyBook = useRef(new Map<string, string>())

  const { draft } = state

  // Мастер записи не выбирается: он известен по карточке сотрудника вошедшего.
  useEffect(() => {
    if (data.employeeId !== null && draft.employeeId !== data.employeeId) {
      dispatch({ type: "employee", employeeId: data.employeeId })
    }
  }, [data.employeeId, draft.employeeId])

  // Услуга по умолчанию — первая из своих: без неё нечего считать по времени,
  // а пустой список времени читается как «свободного времени нет», что было бы враньём.
  const firstService = services.items[0]
  useEffect(() => {
    if (draft.serviceIds.length === 0 && firstService !== undefined) {
      dispatch({
        type: "services",
        serviceIds: [firstService.id],
        durationMinutes: firstService.durationMinutes,
      })
    }
  }, [draft.serviceIds, firstService])

  const windows = useMemo(() => {
    if (data.result === null || data.employeeId === null || draft.durationMinutes === 0) return []
    return freeWindows({
      result: data.result,
      date: data.date,
      employeeId: data.employeeId,
      durationMinutes: draft.durationMinutes,
      stepMinutes: data.result.branch.slotStepMinutes,
      notBeforeMinute: data.nowMinute,
      limit: 6,
    })
  }, [data.result, data.employeeId, data.date, data.nowMinute, draft.durationMinutes])

  // Время по умолчанию — первое свободное. Если форму открыли из промежутка ленты,
  // время уже выбрано этим нажатием и переспрашивать его незачем.
  const firstWindow = windows[0]
  useEffect(() => {
    if (draft.startMinute === 0 && firstWindow !== undefined) {
      dispatch({ type: "start-minute", minute: firstWindow })
    }
  }, [draft.startMinute, firstWindow])

  // ─────────── поиск клиента по ПОЛНОМУ номеру ───────────

  const phone = draft.phone
  const complete = isCompletePhone(phone)

  useEffect(() => {
    // ГЛАВНАЯ СТРОКА ЭКРАНА: пока номер не полон, запроса нет. Не «есть, но пустой».
    if (!complete) return undefined

    const normalized = normalizePhone(phone)
    if (normalized === null) return undefined

    const controller = new AbortController()
    dispatch({ type: "lookup-started" })

    const run = async (): Promise<void> => {
      try {
        // limit 2 — ровно столько, чтобы отличить «одна карточка» от «несколько».
        // Больше двух строк этой форме не нужно ни в одном исходе.
        const result = await fetchClients({ search: normalized, limit: 2 }, controller.signal)
        if (controller.signal.aborted) return
        if (result.items.length > 1) {
          dispatch({
            type: "lookup-failed",
            message:
              "У этого номера несколько карточек — такую запись оформит администратор.",
          })
          return
        }
        dispatch({ type: "lookup-result", clients: result.items })
      } catch (error) {
        if (controller.signal.aborted) return
        const problem = isApiProblem(error) ? error : transportProblem("unknown")
        dispatch({
          type: "lookup-failed",
          message:
            problem.status === 403
              ? "Поиск клиента закрыт салоном. Запись оформит администратор."
              : "Клиента не удалось проверить. Повторите, когда появится связь.",
        })
      }
    }
    void run()

    return () => {
      controller.abort()
    }
  }, [phone, complete])

  const phase: LookupPhase = ((): LookupPhase => {
    if (state.client.kind === "searching") return "searching"
    if (state.client.kind === "found") return "found"
    if (state.client.kind === "new") return "new"
    if (state.client.kind === "many") return "many"
    if (state.client.kind === "failed") return "failed"
    return phone.trim() === "" ? "idle" : "partial"
  })()

  const showRest = phase === "found" || phase === "new"

  // ─────────── отправка ───────────

  const submit = useCallback(() => {
    const normalized = normalizePhone(draft.phone)
    if (draft.serviceIds.length === 0 || data.branch === null || data.employeeId === null) return
    if (draft.clientId === null && normalized === null) return

    const intent = draftIntent(draft)
    const existing = keyBook.current.get(intent)
    const key = existing ?? newIdempotencyKey("create")
    keyBook.current.set(intent, key)

    const request: CabinetCreateAppointmentRequest = {
      branchId: data.branch.id,
      employeeId: data.employeeId,
      startAt: zonedInstant(draft.date, draft.startMinute, data.timeZone),
      items: draft.serviceIds.map((serviceId) => ({ serviceId })),
      client:
        draft.clientId !== null
          ? { clientId: draft.clientId }
          : {
              displayName: draft.clientName.trim(),
              phone: normalized ?? "",
              consents: {
                personalData: true,
                personalDataVersion: CONSENT_VERSION,
                // Рекламное согласие — ОТДЕЛЬНЫЙ документ. Версия уходит только вместе
                // с самим согласием: контракт требует её ровно в этом случае.
                ads: draft.consentAds,
                ...(draft.consentAds ? { adsVersion: CONSENT_VERSION } : {}),
                capturedVia: "verbal",
              },
            },
      ...(draft.comment.trim() === "" ? {} : { comment: draft.comment.trim() }),
      // Подтверждаются только ИЗВЕСТНЫЕ коды. Незнакомый код сервер отвергнет целиком,
      // и человек получит отказ вместо записи — из-за предупреждения, которого ещё
      // не было в этой версии кабинета. Лучше не подтвердить лишнее, чем не записать.
      acknowledgedWarnings: state.warnings
        .map((warning) => warning.code)
        .filter((code): code is CabinetWarningCode =>
          (WarningCodes.options as readonly string[]).includes(code),
        ),
      // Мастер записывает человека, который стоит перед ним: подтверждение ему приходит
      // сразу, как и при записи администратором «с отправкой подтверждения».
      notifyClient: true,
    }

    dispatch({ type: "submit-started" })
    setSubmitProblem(null)

    const send = async (): Promise<void> => {
      try {
        await createAppointment(request, key)
        keyBook.current.delete(intent)
        setCreated(true)
      } catch (error) {
        const problem = isApiProblem(error) ? error : transportProblem("unknown")

        // «СЛОТ ЗАНЯЛИ» — ШТАТНЫЙ ИСХОД, А НЕ СБОЙ. Кабинетное создание отвечает на гонку
        // за время кодом busy_conflicts, доменные операции — slot_taken; это одно и то же
        // событие, и форма обязана вести себя одинаково в обоих случаях.
        if (problem.code === "busy_conflicts" || problem.code === "slot_taken") {
          dispatch({
            type: "slot-taken",
            takenLabel: formatMinute(draft.startMinute),
            employeeName: "",
          })
          // Окна ищутся ОТДЕЛЬНЫМ шагом после отказа, а не приходят вместе с ним:
          // день надо перечитать, иначе предложенное время окажется тем же занятым.
          data.reload()
          return
        }

        if (problem.code === "confirmation_required" && problem.warnings.length > 0) {
          dispatch({
            type: "warnings",
            warnings: problem.warnings.map((warning) => ({
              code: warning.code,
              message: warning.message,
            })),
          })
          return
        }

        dispatch({ type: "submit-failed", message: problem.detail ?? problem.title })
        setSubmitProblem(problem.detail ?? problem.title)
      }
    }
    void send()
  }, [draft, data, state.warnings])

  // После перечитывания дня подставляем в отказ найденные окна: это второе состояние
  // сценария «слот заняли» — «окна нашлись» либо «свободного времени не осталось».
  const conflictOpen = state.conflict !== null
  const searchKind = state.conflict?.search.kind ?? null
  useEffect(() => {
    if (!conflictOpen || searchKind !== "searching") return
    if (data.loading) return
    if (windows.length === 0) {
      dispatch({ type: "windows-none", next: null })
      return
    }
    dispatch({
      type: "windows-found",
      windows: windows.map((minute) => ({
        date: data.date,
        minute,
        employeeId: data.employeeId ?? "",
        label: formatMinute(minute),
      })),
    })
  }, [conflictOpen, searchKind, data.loading, windows, data.date, data.employeeId])

  const goDay = useCallback(() => {
    void navigate({ to: "/my/day", search: { date: data.date } })
  }, [navigate, data.date])

  useEffect(() => {
    if (created) goDay()
  }, [created, goDay])

  // ─────────── состояния, в которых формы нет вовсе ───────────

  if (data.employeeId === null) {
    return (
      <MasterFrame active="day">
        <MasterNoEmployee title="Записать к себе" />
      </MasterFrame>
    )
  }

  if (!canCreate) {
    return (
      <MasterFrame active="day">
        <MasterBackBar to="/my/day" label="Мой день" />
        <EmptyState
          className={styles.center}
          title="Записывать клиентов вам не открыто"
          text="Право записывать выдаёт владелец или управляющий салона. Ваше расписание продолжает работать."
        />
      </MasterFrame>
    )
  }

  // Без связи форма НЕ ОТКРЫВАЕТСЯ вовсе — и это не строгость: данные о свободном времени
  // устарели, а запись, созданная поверх занятого времени, неразрешимо конфликтует
  // с реальной занятостью (GAPS D4). Честнее сказать это сразу, чем принять ввод и потерять.
  if (!data.availability.canEdit) {
    return (
      <MasterFrame active="day" stripe={data.availability.stripe} onStripeAction={data.reload}>
        <MasterBackBar to="/my/day" label="Мой день" />
        <div className={styles.center}>
          <p className={styles.offlineTitle}>Без связи записать нельзя</p>
          <p className={styles.offlineText}>
            Данные о свободном времени могут быть устаревшими. Запишите время на бумаге
            и оформите, когда связь вернётся.
          </p>
          <Button onClick={goDay}>Вернуться к дню</Button>
        </div>
      </MasterFrame>
    )
  }

  const employeeName = data.column?.employee.displayName ?? ""
  // У мастера услуга по-прежнему одна: свой день он записывает быстро и на себя.
  // Список в черновике общий с кабинетом — здесь в нём ровно один элемент.
  const selectedService =
    services.items.find((item) => item.id === draft.serviceIds[0]) ?? null
  const canSend =
    draft.serviceIds.length > 0 &&
    draft.startMinute > 0 &&
    !state.submitting &&
    (draft.clientId !== null ||
      (phase === "new" && draft.clientName.trim() !== "" && draft.consentPersonalData))

  return (
    <MasterFrame active="day" stripe={data.availability.stripe} onStripeAction={data.reload}>
      <div className={styles.head}>
        <MasterBackBar to="/my/day" label="Мой день" />
        <div className={styles.headText}>
          <p className={styles.title}>Записать к себе</p>
          {/* Мастер — ФАКТ, а не выключенное поле: выбора здесь нет, и показывать
              пустой выпадающий список значило бы обещать его. */}
          <p className={styles.subtitle}>
            Мастер — вы{employeeName === "" ? "" : `, ${employeeName}`} ·{" "}
            {formatDateLong(data.date, data.timeZone)}
          </p>
        </div>
      </div>

      <div className={styles.body}>
        {state.conflict === null ? null : (
          <SlotTakenBlock
            takenLabel={state.conflict.takenLabel}
            search={state.conflict.search}
            onPick={(minute) => {
              dispatch({
                type: "pick-window",
                window: {
                  date: data.date,
                  minute,
                  employeeId: data.employeeId ?? "",
                  label: formatMinute(minute),
                },
              })
            }}
            onRetry={() => {
              dispatch({ type: "windows-searching" })
              data.reload()
            }}
          />
        )}

        <div className={styles.field}>
          <div className={styles.fieldHead}>
            <label className={styles.label} htmlFor="master-book-phone">
              Телефон клиента — целиком
            </label>
            <Link to="/my/clients" search={{ q: undefined }} className={styles.myClients}>
              Мои клиенты
            </Link>
          </div>
          <input
            id="master-book-phone"
            className={cx(styles.input, phase === "partial" ? styles.inputHint : undefined)}
            inputMode="tel"
            autoComplete="tel"
            value={draft.phone}
            placeholder="+7 900 000-00-00"
            onChange={(event) => {
              dispatch({ type: "phone", value: event.currentTarget.value })
            }}
          />

          {/* Запрещённая формулировка — «Ничего не найдено, уточните запрос»: она
              приглашает перебирать и подсказывает, что база рядом (ТЗ §2.7.1.1). */}
          {phase === "partial" ? (
            <p className={styles.hint}>Введите номер целиком — по части номера поиск не работает.</p>
          ) : null}
          {phase === "searching" ? <p className={styles.hint}>Ищем по точному номеру…</p> : null}

          {phase === "found" && state.client.kind === "found" ? (
            <div className={styles.clientCard}>
              <div className={styles.avatar} aria-hidden="true">
                {initials(state.client.client.displayName)}
              </div>
              <div className={styles.clientText}>
                <p className={styles.clientName}>{state.client.client.displayName}</p>
                <p className={styles.clientSub}>
                  Ваш клиент · {visitsLabel(state.client.client.visitsCount)} ·{" "}
                  {noShowLabel(state.client.client.noShowCount)}
                </p>
              </div>
              <span className={styles.check} aria-hidden="true">
                ✓
              </span>
            </div>
          ) : null}

          {/* «Несколько карточек на один номер» — единственный исход, которого нет
              в макете, и здесь он разобран явно: мастеру НЕ показывается список, из
              которого он всё равно не может выбрать правильно. Такую запись оформляет
              администратор — у него есть карточки клиентов и история визитов. */}
          {phase === "many" || phase === "failed" ? (
            <p className={styles.failure} role="status">
              {state.client.kind === "failed"
                ? state.client.message
                : "У этого номера несколько карточек — такую запись оформит администратор."}
            </p>
          ) : null}

          {phase === "new" ? (
            <>
              <p className={styles.newNote}>
                Такого номера среди клиентов нет. Запишем как нового — введите имя.
              </p>
              <input
                className={styles.input}
                placeholder="Имя со слов клиента"
                value={draft.clientName}
                aria-label="Имя со слов клиента"
                onChange={(event) => {
                  dispatch({ type: "client-name", value: event.currentTarget.value })
                }}
              />
              {/* ДВА РАЗНЫХ ДОКУМЕНТА И ДВЕ РАЗНЫЕ ОТМЕТКИ. Объединять их в один флажок
                  нельзя: это нарушение закона, и штраф за него платит салон. */}
              <div className={styles.consents}>
                <Checkbox
                  checked={draft.consentPersonalData}
                  onChange={(value) => {
                    dispatch({ type: "consent-personal", value })
                  }}
                >
                  {CONSENT_LABEL}
                </Checkbox>
                <div className={styles.consentSplit} />
                <Checkbox
                  checked={draft.consentAds}
                  onChange={(value) => {
                    dispatch({ type: "consent-ads", value })
                  }}
                >
                  Согласие на рекламу — отдельно, по умолчанию нет
                </Checkbox>
              </div>
            </>
          ) : null}
        </div>

        {showRest ? (
          <>
            <div className={styles.field}>
              <span className={styles.label}>Услуга — из ваших</span>
              {services.loading ? (
                <Skeleton width="100%" height={48} radius={12} />
              ) : services.problem !== null ? (
                <p className={styles.failure}>
                  Список ваших услуг не загрузился. Записать без услуги нельзя — попросите
                  администратора открыть каталог.
                </p>
              ) : services.noneAssigned ? (
                <p className={styles.failure}>
                  Вам не отмечено ни одной услуги. Их отмечает администратор — без этого
                  записать к себе нельзя.
                </p>
              ) : (
                services.items.map((service) => (
                  <button
                    key={service.id}
                    type="button"
                    className={cx(
                      styles.option,
                      service.id === draft.serviceIds[0] ? styles.optionActive : undefined,
                    )}
                    aria-pressed={service.id === draft.serviceIds[0]}
                    onClick={() => {
                      dispatch({
                        type: "services",
                        serviceIds: [service.id],
                        durationMinutes: service.durationMinutes,
                      })
                    }}
                  >
                    <span className={styles.optionText}>
                      <span className={styles.optionName}>{service.name}</span>
                      <span className={styles.optionSub}>{service.durationMinutes} мин</span>
                    </span>
                    {/* Цены в форме нет вовсе, когда салон не открыл деньги мастеру:
                        это отсутствие блока, а не прочерк на его месте. */}
                    {data.visibility.money && service.priceMinor !== null ? (
                      <span className={styles.optionPrice}>{formatMoney(service.priceMinor)}</span>
                    ) : null}
                  </button>
                ))
              )}
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Время — свободное в вашей смене</span>
              <div className={styles.times}>
                {windows.length === 0 ? (
                  <p className={styles.hint}>
                    Свободного времени в вашей смене на этот день не осталось.
                  </p>
                ) : (
                  windows.map((minute) => (
                    <button
                      key={minute}
                      type="button"
                      className={cx(
                        styles.slot,
                        minute === draft.startMinute ? styles.slotActive : undefined,
                      )}
                      aria-pressed={minute === draft.startMinute}
                      onClick={() => {
                        dispatch({ type: "start-minute", minute })
                      }}
                    >
                      {formatMinute(minute)}
                    </button>
                  ))
                )}
              </div>
              <p className={styles.hint}>Чужие колонки в подборе не участвуют</p>
            </div>
          </>
        ) : null}

        {state.warnings.length === 0 ? null : (
          <div className={styles.warnings} role="status">
            {state.warnings.map((warning) => (
              <p key={warning.code} className={styles.warningText}>
                {warning.message}
              </p>
            ))}
            <p className={styles.hint}>
              Нажмите «Записать» ещё раз — запись создастся с учётом этого.
            </p>
          </div>
        )}

        {submitProblem === null ? null : (
          <p className={styles.failure} role="alert">
            {submitProblem}
          </p>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          variant="primary"
          block
          className={styles.submit}
          loading={state.submitting}
          loadingLabel="Записываем…"
          deniedReason={canSend ? undefined : "Сначала клиент"}
          onClick={submit}
        >
          {canSend && draft.startMinute > 0
            ? `Записать на ${formatMinute(draft.startMinute)}`
            : "Сначала клиент"}
        </Button>
        {selectedService === null || !data.visibility.money || selectedService.priceMinor === null ? null : (
          <p className={styles.footerNote}>
            {selectedService.name} · {formatMoney(selectedService.priceMinor)}
          </p>
        )}
      </div>
    </MasterFrame>
  )
}

function initials(displayName: string): string {
  return displayName
    .trim()
    .split(/\s+/u)
    .slice(0, 2)
    .map((word) => word.slice(0, 1).toUpperCase())
    .join("")
}

/**
 * 2-2.10.2 «Это время только что заняли» глазами мастера.
 *
 * ВВЕДЁННОЕ ОСТАЁТСЯ НА МЕСТЕ: блок появляется НАД полями, а не вместо них, и меняет
 * ровно одно — время. Это и есть смысл всего сценария: человек стоит перед мастером,
 * и форма, стёршая его имя и услугу, кончается записью на бумажке.
 */
function SlotTakenBlock(props: {
  readonly takenLabel: string
  readonly search: WindowSearch
  readonly onPick: (minute: number) => void
  readonly onRetry: () => void
}): ReactNode {
  const { search } = props

  return (
    <div className={styles.taken}>
      <p className={styles.takenTitle}>Это время только что заняли</p>
      <p className={styles.takenText}>
        Пока вы оформляли запись, {props.takenLabel} занял другой клиент. Введённое
        сохранено — выберите другое время.
      </p>

      {search.kind === "searching" ? (
        <div className={styles.takenRow} aria-label="Ищем ближайшие окна">
          <Skeleton width={64} height={26} radius={8} />
          <Skeleton width={64} height={26} radius={8} />
          <Skeleton width={64} height={26} radius={8} />
          <span className={styles.hint}>Ищем ближайшие окна…</span>
        </div>
      ) : null}

      {search.kind === "found" ? (
        <div className={styles.takenRow}>
          <span className={styles.hint}>Ближайшее у вас:</span>
          {search.windows.map((window, index) => (
            <button
              key={`${window.date}-${String(window.minute)}`}
              type="button"
              className={cx(styles.slot, index === 0 ? styles.slotActive : undefined)}
              onClick={() => {
                props.onPick(window.minute)
              }}
            >
              {window.label}
            </button>
          ))}
        </div>
      ) : null}

      {search.kind === "none" ? (
        <p className={styles.takenText}>
          Свободного времени в вашей смене на сегодня не осталось. Выберите другой день
          стрелками в «Моём дне».
        </p>
      ) : null}

      {search.kind === "failed" ? (
        <div className={styles.takenRow}>
          <span className={styles.failure}>Свободное время не подгрузилось. Введённое сохранено.</span>
          <Button size="sm" onClick={props.onRetry}>
            Повторить
          </Button>
        </div>
      ) : null}
    </div>
  )
}
